import type { ReactNode } from 'react';
import styles from './tile.module.scss';

export type TileActionsProps = {
  /** Action buttons/content to display */
  children: ReactNode;
  /** Additional class name */
  className?: string;
};

/**
 * TileActions - Footer actions area (buttons, toggles, etc.)
 */
export function TileActions({ children, className }: TileActionsProps) {
  return (
    <div className={`${styles.tileActions} ${className || ''}`}>{children}</div>
  );
}

export type TileMenuProps = {
  /** Menu items/content */
  children: ReactNode;
  /** Additional class name */
  className?: string;
};

/**
 * TileMenu - Dropdown menu container (positioned at top-right corner)
 */
export function TileMenu({ children, className }: TileMenuProps) {
  return (
    <div className={`${styles.tileMenu} ${className || ''}`}>{children}</div>
  );
}

export type TileFavouriteButtonProps = {
  /** Whether the item is favourited */
  isFavourited: boolean;
  /** Handler for favourite toggle */
  onToggle: () => void;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Additional class name */
  className?: string;
};

/**
 * TileFavouriteButton - Heart button for favouriting items
 */
export function TileFavouriteButton({
  isFavourited,
  onToggle,
  disabled = false,
  className
}: TileFavouriteButtonProps) {
  return (
    <button
      type="button"
      className={`${styles.tileFavouriteButton} ${isFavourited ? styles.tileFavouriteButtonActive : ''} ${className || ''}`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggle();
      }}
      disabled={disabled}
      aria-label={isFavourited ? 'Remove from favourites' : 'Add to favourites'}
      aria-pressed={isFavourited}
    >
      <svg
        viewBox="0 0 24 24"
        fill={isFavourited ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="2"
        className={styles.heartIcon}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
        />
      </svg>
    </button>
  );
}

export type TileReservedBannerProps = {
  /** Banner text */
  text?: string;
  /** Additional class name */
  className?: string;
};

/**
 * TileReservedBanner - Diagonal ribbon overlay for reserved/rehomed items.
 * Matches the legacy diagonal banner style.
 */
export function TileReservedBanner({
  text = 'Reserved',
  className
}: TileReservedBannerProps) {
  return (
    <div className={`${styles.tileReservedBanner} ${className || ''}`}>
      <span className={styles.tileReservedBannerText}>{text}</span>
    </div>
  );
}
